import type { AxiosError } from 'axios'
import { v4 } from 'uuid'
import {
  type ChatPlaygroundState,
  type ChatPlaygroundTurnBody,
  chatPlaygroundService,
  type RChatPlaygroundEnvelope,
} from 'v2/shared/services/chat-playground-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'

// Routes the builder's test chat the way the widget's controller does (packages/chat-client:
// core/controller.ts connect, core/conversation-boundary.ts, transports/sse.ts and
// transports/runner-http.ts). Rendering is not done here: the runner's node events and
// agent-runner's chat lines reach the playground stream, which the run panel reads. Chat lines the
// runner writes itself (preset text, a reply to a preset, the AI node's static greeting) are also
// published to the playground stream on playground runs, so the panel shows them. The one
// exception is the apology the runner writes when a chat walk reaches a dead end (chat_walk.py);
// that goes to the run log only.

export interface ChatPlaygroundSession {
  sessionId: string
  state: ChatPlaygroundState
}

export interface ChatPlaygroundResult {
  session: ChatPlaygroundSession
  error?: string
}

interface TurnOutcome {
  status?: number
  error?: string
  state?: ChatPlaygroundState
  continued?: boolean
}

const CONVERSATION_MOVED = 410
const NOT_ACCEPTING = 'This test conversation is no longer taking replies. Start a new test.'

const sessionOf = (envelope: RChatPlaygroundEnvelope): ChatPlaygroundSession => ({
  sessionId: envelope.session_id,
  state: envelope.bootstrap_state,
})

const statusOf = (error: unknown): number | undefined => {
  const e = error as { status?: number; response?: { status?: number } }
  return e?.status ?? e?.response?.status
}

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}

// Mirrors boundaryFromFrame (transports/sse-frame.ts) plus the error and closed frames.
const readFrame = (outcome: TurnOutcome, raw: unknown) => {
  const frame = record(raw)
  const content = record(frame.content)
  if (frame.type === 'error') {
    outcome.error = typeof content.message === 'string' ? content.message : 'The agent failed.'
  } else if (frame.type === 'directive') {
    if (content.input === 'await_reply') outcome.state = 'preset_wait'
    if (content.input === 'continue') outcome.continued = true
  } else if (frame.type === 'signal') {
    const name = content.event_name ?? content.eventName
    if (name === 'CONVERSATION_CLOSED') outcome.state = 'terminal'
    if (name === 'AGENT_ASSIGNING') outcome.state = 'human'
  }
}

const openTurn = (sessionId: string, body: ChatPlaygroundTurnBody): Promise<TurnOutcome> =>
  new Promise((resolve) => {
    const outcome: TurnOutcome = {}
    chatPlaygroundService.turn(sessionId, body, {
      onMessage: (frame) => readFrame(outcome, frame),
      onError: (error) => {
        outcome.status = statusOf(error)
        outcome.error ??= error instanceof Error ? error.message : 'The agent failed.'
      },
      onDone: () => resolve(outcome),
    })
  })

const reconcile = async (session: ChatPlaygroundSession) =>
  sessionOf(await chatPlaygroundService.state(session.sessionId))

const turn = async (
  session: ChatPlaygroundSession,
  body: ChatPlaygroundTurnBody,
  mayReconcile: boolean,
): Promise<ChatPlaygroundResult> => {
  const outcome = await openTurn(session.sessionId, body)
  // 410 is refused before the agent reads the message, so it is safe to send again on the new leg.
  if (outcome.status === CONVERSATION_MOVED && mayReconcile) {
    return route(await reconcile(session), body, false)
  }
  if (outcome.error) return { session, error: outcome.error }
  const next = { ...session, state: outcome.state ?? session.state }
  // The AI fired an intent and the runner walked into another AI node, which answers on a trigger.
  if (outcome.continued) return turn(next, { trigger: true }, false)
  return { session: next }
}

const presetTurn = async (
  session: ChatPlaygroundSession,
  body: ChatPlaygroundTurnBody,
  mayReconcile: boolean,
): Promise<ChatPlaygroundResult> => {
  let next: ChatPlaygroundSession
  try {
    next = sessionOf(await chatPlaygroundService.presetTurn(session.sessionId, body))
  } catch (error) {
    if (statusOf(error) === CONVERSATION_MOVED && mayReconcile) {
      return route(await reconcile(session), body, false)
    }
    return { session, error: getErrorText(error as AxiosError) }
  }
  // The reply moved the walk onto an AI node; the agent only speaks on a turn.
  if (next.state === 'ai') return turn(next, { trigger: true }, false)
  return { session: next }
}

const route = (
  session: ChatPlaygroundSession,
  body: ChatPlaygroundTurnBody,
  mayReconcile: boolean,
): Promise<ChatPlaygroundResult> => {
  if (session.state === 'ai') return turn(session, body, mayReconcile)
  if (session.state === 'preset_wait') return presetTurn(session, body, mayReconcile)
  return Promise.resolve({ session, error: NOT_ACCEPTING })
}

/** Starts the run with its first message. `onSession` fires as soon as the session exists. */
export const startChatPlayground = async (
  phloId: string,
  message: string,
  onSession: (session: ChatPlaygroundSession) => void,
): Promise<ChatPlaygroundResult> => {
  const first = { message, message_id: v4() }
  const session = sessionOf(await chatPlaygroundService.bootstrap({ phlo_id: phloId, ...first }))
  onSession(session)
  // A runner leg consumes the first message during bootstrap; an AI park does not, so it is
  // replayed as the first turn under the same id.
  if (session.state !== 'ai') return { session }
  return sendTurn(session, first)
}

export const sendChatPlaygroundMessage = (
  session: ChatPlaygroundSession,
  message: string,
  attachments?: object[],
): Promise<ChatPlaygroundResult> =>
  sendTurn(session, { message, message_id: v4(), ...(attachments && { attachments }) })

const sendTurn = async (
  session: ChatPlaygroundSession,
  body: ChatPlaygroundTurnBody,
): Promise<ChatPlaygroundResult> => {
  try {
    return await route(session, body, true)
  } catch (error) {
    return { session, error: getErrorText(error as AxiosError) }
  }
}
